/**
 * WellsPage — Task 25.4 (DEC-115). PLATFORM_ADMIN list of platform catalog wells.
 *
 * Search (name + API number), a filter bar (state, county, type, status, source,
 * active), a paginated table (50/page), and an Add Well modal. Row → well detail.
 */
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '@lodex-pro/web-shared';
import { listFrom, useUrlNumber, useUrlParam, useUrlSort } from '../../lib/url-state';
import { SortableHead } from 'src/components/ui/sortable-head';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { AddButton, Pill, searchInputStyle, subText, fieldStyle } from '../../ui/bits';
import { useWells, PAGE_SIZE, type WellFilters } from './hooks';
import { AddWellModal } from './AddWellModal';
import { STATES, WELL_STATUSES } from './AddWellModal';
import { WELL_TYPE_EXACT_OPTIONS, WELL_TYPE_FILTER_OPTIONS, wellTypeLabel } from './well-type';
import { formatNumber } from '@lodex-pro/shared/number-format';

const SOURCES = ['OCC', 'RRC', 'MANUAL'] as const;

/** The columns the wells API sorts by (OPEN-204); the API refuses any other. */
const WELL_SORTS = ['wellName', 'apiNumber', 'county', 'haulerCount'] as const;

export function WellsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  // Corbin 2026-10-20: the search, filters and page live in the URL (lib/url-state.ts); a filter
  // change goes back to page 1 there.
  const [search, setSearch] = useUrlParam('q');
  const [state, setState] = useUrlParam('state');
  const [county, setCounty] = useUrlParam('county');
  const [wellType, setWellType] = useUrlParam('type');
  const [wellStatus, setWellStatus] = useUrlParam('wellStatus');
  const [source, setSource] = useUrlParam('source');
  const [activeParam, setActive] = useUrlParam('status', 'all');
  const active = activeParam as 'all' | 'active' | 'inactive';
  const [page, setPage] = useUrlNumber('page');
  // OPEN-204 — ~132k wells, so the API sorts and pages; a new sort goes back to page 1.
  const { sort, toggle } = useUrlSort(WELL_SORTS, { key: 'wellName', dir: 'asc' });
  const [modalOpen, setModalOpen] = React.useState(false);

  // OPEN-194: the boxes show every keystroke; the request waits for a pause in typing.
  const q = useDebouncedValue(search.trim());
  const countyQ = useDebouncedValue(county.trim());

  const filters: WellFilters = {
    ...(q !== '' && { q }),
    ...(state !== '' && { state }),
    ...(countyQ !== '' && { county: countyQ }),
    ...(wellType !== '' && { wellType }),
    ...(wellStatus !== '' && { wellStatus }),
    ...(source !== '' && { source }),
    ...(active === 'active' && { isActive: true }),
    ...(active === 'inactive' && { isActive: false }),
    sort: sort?.key ?? 'wellName',
    ...(sort !== null && { dir: sort.dir }),
  };

  // Reset to page 1 whenever a filter changes.

  const { data, isLoading, isError } = useWells(filters, page * PAGE_SIZE);
  const wells = data?.wells ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectStyle = { ...fieldStyle, minHeight: '38px', maxWidth: '130px' };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Wells</h2>
          <p style={{ ...subText, marginTop: '2px' }}>{formatNumber(total)} total</p>
        </div>
        <AddButton label="Add Well" onClick={(): void => setModalOpen(true)} />
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e): void => setSearch(e.target.value)}
          placeholder="Search name or API number…"
          style={{ ...searchInputStyle, maxWidth: '260px' }}
          aria-label="Search wells"
        />
        <select
          value={state}
          onChange={(e): void => setState(e.target.value)}
          style={selectStyle}
          aria-label="State"
        >
          <option value="">All states</option>
          {STATES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          value={county}
          onChange={(e): void => setCounty(e.target.value.toUpperCase())}
          placeholder="County"
          style={{ ...fieldStyle, minHeight: '38px', maxWidth: '130px' }}
          aria-label="County"
        />
        <select
          value={wellType}
          onChange={(e): void => setWellType(e.target.value)}
          style={selectStyle}
          aria-label="Well type"
        >
          <option value="">All types</option>
          {WELL_TYPE_FILTER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          <optgroup label="Exact type">
            {WELL_TYPE_EXACT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </optgroup>
        </select>
        <select
          value={wellStatus}
          onChange={(e): void => setWellStatus(e.target.value)}
          style={selectStyle}
          aria-label="Well status"
        >
          <option value="">All statuses</option>
          {WELL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={source}
          onChange={(e): void => setSource(e.target.value)}
          style={selectStyle}
          aria-label="Source"
        >
          <option value="">All sources</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          value={active}
          onChange={(e): void => setActive(e.target.value)}
          style={selectStyle}
          aria-label="Active status"
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {isLoading && <p style={subText}>Loading wells…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load wells. Refresh to try again.
        </p>
      )}

      {!isLoading && !isError && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHead column="wellName" sort={sort} onSort={toggle}>
                  Well Name
                </SortableHead>
                <SortableHead column="apiNumber" sort={sort} onSort={toggle}>
                  API Number
                </SortableHead>
                <TableHead>Operator</TableHead>
                <SortableHead column="county" sort={sort} onSort={toggle}>
                  County
                </SortableHead>
                <TableHead>Type</TableHead>
                <SortableHead column="haulerCount" sort={sort} onSort={toggle}>
                  Haulers
                </SortableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {wells.map((w) => (
                <TableRow
                  key={w.id}
                  onClick={(): void =>
                    void navigate(`/wells/${w.id}`, { state: listFrom(location) })
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell style={{ fontWeight: 500 }}>{w.wellName}</TableCell>
                  <TableCell style={subText}>{w.apiNumber}</TableCell>
                  <TableCell>{w.operatorName ?? <span style={subText}>—</span>}</TableCell>
                  <TableCell>{w.county ?? <span style={subText}>—</span>}</TableCell>
                  <TableCell>
                    {wellTypeLabel(w.wellType) ?? <span style={subText}>—</span>}
                  </TableCell>
                  <TableCell>{formatNumber(w.haulerCount)}</TableCell>
                  <TableCell>
                    <Pill
                      label={w.isActive ? 'Active' : 'Inactive'}
                      tone={w.isActive ? 'positive' : 'muted'}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {wells.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    style={{ ...subText, textAlign: 'center', padding: '32px' }}
                  >
                    No wells match your filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {pageCount > 1 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginTop: '12px',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                type="button"
                variant="outline"
                disabled={page <= 0}
                onClick={(): void => setPage(Math.max(0, page - 1))}
              >
                Previous
              </Button>
              <span style={subText}>
                Page {formatNumber(page + 1)} of {formatNumber(pageCount)}
              </span>
              <Button
                type="button"
                variant="outline"
                disabled={page >= pageCount - 1}
                onClick={(): void => setPage(Math.min(pageCount - 1, page + 1))}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}

      <AddWellModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        onCreated={(id): void => void navigate(`/wells/${id}`)}
      />
    </div>
  );
}
